import './EventDetailPage.scss';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import Spinner from 'Components/Shared/Spinner/Spinner';
import FormattedText from 'Components/Shared/FormattedText/FormattedText';
import WrapperLayout from 'Components/Shared/WrapperLayout/WrapperLayout';
import PerformanceList from 'Pages/EventDetailPage/PerformanceList/PerformanceList';
import EventText from 'Pages/EventDetailPage/EventText/EventText';
import EventButton from 'Pages/EventDetailPage/EventButton/EventButton';
import EventInfoBox from 'Pages/EventDetailPage/EventInfoBox/EventInfoBox';
import EventTitle from 'Pages/EventDetailPage/EventTitle/EventTitle';
import EventVenueModal from 'Pages/EventDetailPage/EventVenueModal/EventVenueModal';
import PerformanceItem from 'Pages/EventDetailPage/PerformanceList/PerformanceItem';
import TitleComponent from 'Helpers/DynamicTitle';

interface IProps {
  isFetching: boolean;
  isFetchFailed: boolean;
  isMobile: boolean;
  isShowingVenueModal: boolean;
  event: Julius.Website.EventDetailViewModel;
  firstDate: Date;
  lastDate: Date;
  minPrice: number;
  onShowVenueInfo: () => void;
  onHideVenueInfo: () => void;
  onBuyTickets: () => void;
  onBuyVoucher: () => void;
  onPerformanceClick: (performanceID: number) => void;
  setPerformanceListRef: (node: HTMLElement) => void;
}

const EventDetailPage: React.SFC<IProps> = ({ 
  isFetching,
  isFetchFailed,
  isMobile,
  isShowingVenueModal,
  event,
  firstDate,
  lastDate,
  minPrice,
  onShowVenueInfo,
  onHideVenueInfo,
  onBuyTickets,
  onBuyVoucher,
  onPerformanceClick,
  setPerformanceListRef,
}) => {
  if (isFetching) {
    return <Spinner height='lg' />;
  }

  if (!event || isFetchFailed) {
    return (
    <WrapperLayout style='event-detail-page' align='top'>
      <FormattedText 
        style='error'
        displayName='Kunde inte hitta eventet.'
        formattedName='event.message.empty'
      />
    </WrapperLayout>
    );
  }

  const { name, oneLiner, shortText, longText, imageUrl, 
    organizer, playLength, playInformation, performances, 
    venueDetails, linkWebsite, linkSpotify, linkYoutube, language } = event;

  const Buttons = () => (
    <>
      { minPrice && 
        <EventButton iconType='tag-o' style='ticket' onClick={ onBuyTickets }>
          <FormattedText 
            style='button-title'
            displayName='Köp biljetter'
            formattedName='event.buytickets'
            />
          <FormattedMessage
            id='global.fromprice'
            defaultMessage=''
            values={ { price: minPrice } }
          />
        </EventButton>
      }
      <EventButton iconType='gift' style='voucher' onClick={ onBuyVoucher }>
        <FormattedText 
          style='button-title'
          displayName='Köp presentkort'
          formattedName='event.buyvoucher'
        />
        <FormattedText
          displayName='Överraska med en upplevelse'
          formattedName='event.surprise'
        />
      </EventButton>
    </>
  );

  const InfoBox = () => (
    <EventInfoBox { ... { 
      firstDate, 
      lastDate, 
      venueDetails, 
      playLength, 
      playInformation, 
      linkWebsite,
      linkSpotify,
      linkYoutube,
      onShowVenueInfo,
      language,
    } } />
  );

  const Text = () => (
    <EventText { ... { 
      shortText, 
      longText,
    } } />
  );

  const Performances = () => (
    performances && performances.length > 0 &&
      <PerformanceList organizer={ organizer } setRef={ setPerformanceListRef }>
        { performances.map(p => 
          <PerformanceItem
            key={ p.id }
            performance={ p } 
            onClick={ onPerformanceClick }
          />)
        }
      </PerformanceList>
  );

  const Image = () => (
    <div className='image-container'>
      <img alt={ name } src={ imageUrl } />
    </div>  
  );

  const VenueModal = () => (
    <EventVenueModal 
      venueDetails={ venueDetails } 
      onClose={ onHideVenueInfo } 
      isVisible={ isShowingVenueModal } 
    />
  );

  if (isMobile) {
    return (
      <WrapperLayout 
        style='event-detail-page-mobile' 
        padding='none' 
        justify='center'
      >
        <TitleComponent title={name}/>
        <Image />
        <div className='main-content'>
          <EventTitle { ... { name, oneLiner } } />
          <InfoBox />
          <Buttons />
          <Text />
        </div>
        <Performances />
        <VenueModal />
      </WrapperLayout>
    );
  }

  return (
    <WrapperLayout 
      style='event-detail-page' 
      padding='none'
    >
      <TitleComponent title={name}/>
      <Image />
      <div className='main-content'>
        <div className='left-content'>
          <EventTitle { ... { name, oneLiner } } />
          <Text />
        </div>
        { performances && performances.length > 0 &&
          <div className='right-content'>
            <Buttons />
            <InfoBox />
          </div>
        }
      </div>
      <Performances />
      <VenueModal />
    </WrapperLayout>
  );
};

export default EventDetailPage;


// WEBPACK FOOTER //
// ./src/pages/EventDetailPage/EventDetailPage.tsx